import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Plus, Edit, Trash2, Calendar, Users, TrendingUp, Clock, DollarSign, Building2 } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { formatAmount } from '@/utils/formatters';
import { SalaryBatchModal } from '@/components/SalaryBatchModal';
import { useI18n } from '@/hooks/useI18n';
import { UsersFourIcon } from '@phosphor-icons/react';

interface SalaryBatch {
    id: string;
    year: number;
    month: number;
    week?: number | null;
    payment_deadline: string;
    total_amount: number;
    employee_count: number;
    has_linked_payments: boolean;
    created_at: string;
}

interface EmployeeSalaryDetail {
    employee_id: string;
    employee_name: string;
    job_id?: string;
    job_title?: string;
    hours_worked: number;
    hourly_rate: number;
    tip_amount: number;
    total_salary: number;
    total_tips: number;
}

interface CostCenterSalaryEstimate {
    cost_center_id: string;
    cost_center_name: string;
    total_hours: number;
    total_salary: number;
    total_tips: number;
    employee_count: number;
    employee_details: EmployeeSalaryDetail[];
}

interface SalaryEstimation {
    period_type: string;
    start_date: string;
    end_date: string;
    total_hours: number;
    total_salary: number;
    total_tips: number;
    cost_centers: CostCenterSalaryEstimate[];
}

function PeopleCostsPage() {
    const { t } = useI18n();
    const [batches, setBatches] = useState<SalaryBatch[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedBatchId, setSelectedBatchId] = useState<string | undefined>(undefined);
    const [baseCurrency, setBaseCurrency] = useState<string>('HUF');
    const [estimatePeriod, setEstimatePeriod] = useState<string>('current-week');
    const [salaryEstimate, setSalaryEstimate] = useState<SalaryEstimation | null>(null);
    const [estimateLoading, setEstimateLoading] = useState<boolean>(false);

    useEffect(() => {
        fetchUserData();
        fetchBatches();
    }, []);

    useEffect(() => {
        fetchSalaryEstimate(estimatePeriod);
    }, [estimatePeriod]);

    const fetchUserData = async () => {
        try {
            const response = await apiFetch('/api/auth/me');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency || 'HUF');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const fetchSalaryEstimate = async (period: string) => {
        setEstimateLoading(true);
        try {
            const endpointMap: Record<string, string> = {
                'current-week': '/api/people/salary-estimates/current-week',
                'current-month': '/api/people/salary-estimates/current-month',
            };
            const endpoint = endpointMap[period] || endpointMap['current-month'];
            const response = await apiFetch(endpoint);
            if (response.ok) {
                const data = await response.json();
                setSalaryEstimate(data);
            }
        } catch (error) {
            console.error('Error fetching salary estimate:', error);
        } finally {
            setEstimateLoading(false);
        }
    };

    const fetchBatches = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await apiFetch('/api/people/salary-batches/?limit=100');
            if (!response.ok) {
                throw new Error(`Failed to fetch salary batches: ${response.status}`);
            }

            const data = await response.json();
            setBatches(data.items || []);
        } catch (err) {
            console.error('Error fetching salary batches:', err);
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleNewBatch = () => {
        setSelectedBatchId(undefined);
        setIsModalOpen(true);
    };

    const handleEditBatch = (batchId: string) => {
        setSelectedBatchId(batchId);
        setIsModalOpen(true);
    };

    const handleDeleteBatch = async (batchId: string) => {
        if (!confirm('Are you sure you want to delete this salary batch?')) {
            return;
        }

        try {
            const response = await apiFetch(`/api/people/salary-batches/${batchId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                fetchBatches();
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail || 'Failed to delete batch'}`);
            }
        } catch (error) {
            console.error('Error deleting batch:', error);
            alert('Failed to delete batch');
        }
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedBatchId(undefined);
    };

    const handleModalSave = () => {
        fetchBatches();
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('hu-HU');
    };

    const formatMonthYear = (year: number, month: number): string => {
        const date = new Date(year, month - 1);
        return date.toLocaleDateString('default', { year: 'numeric', month: 'long' });
    };

    const formatPeriod = (batch: SalaryBatch): string => {
        if (batch.week) {
            return `${batch.year} - ${t('employees.costs.week', 'common')} ${batch.week}`;
        }
        return formatMonthYear(batch.year, batch.month);
    };


    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
                            <UsersFourIcon className="h-8 w-8" />
                            {t('employees.costs.subtitle', 'common')}
                        </h1>
                        <p className="mt-1 text-muted-foreground">
                            {t('employees.costs.description', 'common')}
                        </p>
                    </div>
                    <Button onClick={handleNewBatch}>
                        <Plus className="mr-2 h-4 w-4" />
                        {t('employees.costs.new', 'common')}
                    </Button>
                </div>

                {/* Salary Estimates Section */}
                <Card>
                    <CardHeader className="border-b p-4 max-h-18">
                        <div className="flex items-center justify-items-stretch">
                            <div className="flex-1">
                                <CardTitle className="flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4" />
                                    {t('employees.costs.salaryEstimates', 'common') || 'Salary Estimates'}
                                </CardTitle>
                            </div>
                            <div className="flex-1 justify-items-end">
                                <Tabs value={estimatePeriod} onValueChange={setEstimatePeriod} >
                                    <TabsList>
                                        <TabsTrigger value="current-week">
                                            {t('employees.costs.currentWeek', 'common') || 'Full Week'}
                                        </TabsTrigger>
                                        <TabsTrigger value="current-month">
                                            {t('employees.costs.currentMonth', 'common') || 'Full Month'}
                                        </TabsTrigger>
                                    </TabsList>
                                </Tabs>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        {estimateLoading ? (
                            <div className="py-8 text-center text-muted-foreground">
                                {t('general.loading', 'common') || 'Loading...'}
                            </div>
                        ) : salaryEstimate ? (
                            <div className="space-y-6">
                                {/* Period Info */}
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Calendar className="h-4 w-4" />
                                    <span>
                                        {formatDate(salaryEstimate.start_date)} - {formatDate(salaryEstimate.end_date)}
                                    </span>
                                </div>

                                {/* Summary Cards */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                                            <Clock className="h-4 w-4" />
                                            {t('employees.costs.totalHours', 'common') || 'Total Hours'}
                                        </div>
                                        <div className="text-2xl font-bold text-right">
                                            {Number(salaryEstimate.total_hours).toFixed(1)}
                                        </div>
                                    </div>
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                                            <DollarSign className="h-4 w-4" />
                                            {t('employees.costs.totalSalary', 'common') || 'Total Salary'}
                                        </div>
                                        <div className="text-2xl font-bold text-right">
                                            {formatAmount(Number(salaryEstimate.total_salary), { currencyCode: baseCurrency })}
                                        </div>
                                    </div>
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                                            <DollarSign className="h-4 w-4" />
                                            {t('employees.costs.totalTips', 'common') || 'Total Tips'}
                                        </div>
                                        <div className="text-2xl font-bold text-right">
                                            {formatAmount(Number(salaryEstimate.total_tips), { currencyCode: baseCurrency })}
                                        </div>
                                    </div>
                                    <div className="p-4 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                                            <Building2 className="h-4 w-4" />
                                            {t('employees.costs.costCenters', 'common') || 'Cost Centers'}
                                        </div>
                                        <div className="text-2xl font-bold text-right">
                                            {salaryEstimate.cost_centers.length}
                                        </div>
                                    </div>
                                </div>

                                {/* Cost Center Breakdown */}
                                {salaryEstimate.cost_centers.length > 0 && (
                                    <div>
                                        <h4 className="text-sm font-medium mb-3">
                                            {t('employees.costs.byCostCenter', 'common') || 'By Cost Center'}
                                        </h4>
                                        <div className="border rounded-lg overflow-hidden">
                                            <Table>
                                                <TableHeader>
                                                    <TableRow>
                                                        <TableHead>{t('employees.costs.costCenter', 'common') || 'Cost Center'}</TableHead>
                                                        <TableHead className="text-right">{t('employees.costs.employees', 'common') || 'Employees'}</TableHead>
                                                        <TableHead className="text-right">{t('units.hours', 'common') || 'Hours'}</TableHead>
                                                        <TableHead className="text-right">{t('employees.costs.salary', 'common') || 'Salary'}</TableHead>
                                                        <TableHead className="text-right">{t('employees.costs.tips', 'common') || 'Tips'}</TableHead>
                                                        <TableHead className="text-right">{t('employees.costs.total', 'common') || 'Total'}</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {salaryEstimate.cost_centers.map((cc) => (
                                                        <TableRow key={cc.cost_center_id}>
                                                            <TableCell className="font-medium">
                                                                <div className="flex items-center gap-2">
                                                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                                                    {cc.cost_center_name}
                                                                </div>
                                                            </TableCell>
                                                            <TableCell className="text-right">{cc.employee_count}</TableCell>
                                                            <TableCell className="text-right">{Number(cc.total_hours).toFixed(1)}</TableCell>
                                                            <TableCell className="text-right">
                                                                {formatAmount(Number(cc.total_salary), { currencyCode: baseCurrency })}
                                                            </TableCell>
                                                            <TableCell className="text-right">
                                                                {formatAmount(Number(cc.total_tips), { currencyCode: baseCurrency })}
                                                            </TableCell>
                                                            <TableCell className="text-right font-medium">
                                                                {formatAmount(Number(cc.total_salary) + Number(cc.total_tips), { currencyCode: baseCurrency })}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="py-8 text-center text-muted-foreground">
                                {t('employees.costs.noEstimateData', 'common') || 'No timesheet data available for this period'}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {error && (
                    <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
                        {error}
                    </div>
                )}

                {/* Salary Batches Section */}
                <div>
                    <h3 className="text-lg font-semibold mb-4">
                        {t('employees.costs.salaryBatches', 'common') || 'Salary Batches'}
                    </h3>
                </div>

                {loading ? (
                    <div className="py-12 text-center">
                        <p>Loading...</p>
                    </div>
                ) : batches.length === 0 ? (
                    <div className="py-12 text-center text-muted-foreground">
                        {t('employees.costs.noBatches', 'common')}
                    </div>
                ) : (
                    <div className="">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('employees.costs.period', 'common')}</TableHead>
                                    <TableHead>{t('employees.costs.paymentDeadline', 'common')}</TableHead>
                                    <TableHead className="text-right">{t('employees.costs.employeeCount', 'common')}</TableHead>
                                    <TableHead className="text-right">{t('employees.costs.amount', 'common')}</TableHead>
                                    <TableHead className="text-center">{t('employees.costs.status', 'common')}</TableHead>
                                    <TableHead className="text-right"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {batches.map((batch) => (
                                    <TableRow
                                        key={batch.id}
                                        className="cursor-pointer hover:bg-muted/50"
                                        onClick={() => handleEditBatch(batch.id)}
                                    >
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                                {formatPeriod(batch)}
                                            </div>
                                        </TableCell>
                                        <TableCell>{formatDate(batch.payment_deadline)}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Users className="h-4 w-4 text-muted-foreground" />
                                                {batch.employee_count}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-right font-medium">
                                            {formatAmount(batch.total_amount,  { currencyCode: baseCurrency })}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            {batch.has_linked_payments ? (
                                                <Badge variant="default">{t('employees.costs.paid', 'common')}</Badge>
                                            ) : (
                                                <Badge variant="secondary">{t('employees.costs.pending', 'common')}</Badge>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditBatch(batch.id);
                                                    }}
                                                >
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDeleteBatch(batch.id);
                                                    }}
                                                    disabled={batch.has_linked_payments}
                                                >
                                                    <Trash2 className="h-4 w-4"  />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}

                <SalaryBatchModal
                    isOpen={isModalOpen}
                    onClose={handleModalClose}
                    onSave={handleModalSave}
                    batchId={selectedBatchId}
                />
            </div>
        </Layout>
    );
}

export const Route = createFileRoute('/people/costs')({
    component: PeopleCostsPage
});
